import { supabase } from "@/lib/supabase";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000/api/v1";

export function apiEndpoint(path: string): string {
  return `${API_URL}${path}`;
}

export class ApiError extends Error {
  status: number;
  code: string;
  details: unknown;
  /** The whole structured error body, including any fields beyond
   *  code/message (e.g. what a partial write applied). */
  payload: Record<string, unknown>;
  constructor(
    status: number,
    code: string,
    message: string,
    details: unknown = null,
    payload: Record<string, unknown> = {},
  ) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
    this.payload = payload;
  }
}

type ApiErrorPayload = {
  code?: string;
  message?: string;
  details?: unknown;
  validation_errors?: unknown;
};

async function sessionAccessToken(
  forceRefresh = false,
): Promise<string | null> {
  const { data } = forceRefresh
    ? await supabase.auth.refreshSession()
    : await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

async function errorPayload(res: Response): Promise<ApiErrorPayload> {
  try {
    const body = await res.json();
    // FastAPI HTTPException detail is either a bare string (detail="…") or our
    // structured {code, message}. Normalize both so ApiError.message carries
    // the real reason.
    const detail = (body as { detail?: unknown })?.detail;
    return typeof detail === "string"
      ? { message: detail }
      : detail && typeof detail === "object"
        ? (detail as ApiErrorPayload)
        : (body as ApiErrorPayload);
  } catch {
    return {};
  }
}

function recoverableAuthFailure(res: Response, payload: ApiErrorPayload) {
  return (
    (res.status === 401 || res.status === 503) &&
    ["missing_bearer", "token_expired", "invalid_token"].includes(
      payload.code ?? "",
    )
  );
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const isFormData =
    typeof FormData !== "undefined" && init.body instanceof FormData;
  const explicitHeaders = new Headers(init.headers);
  const explicitAuthorization = explicitHeaders.has("Authorization");
  let token = await sessionAccessToken();
  if (!token && !explicitAuthorization) {
    // A mutation can land during Supabase's token-refresh handoff. One
    // explicit refresh avoids sending a protected write with no bearer.
    token = await sessionAccessToken(true);
  }

  const request = (accessToken: string | null) => {
    const headers = new Headers(init.headers);
    // The browser must add the multipart boundary itself for FormData.
    if (!isFormData && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    if (accessToken && !explicitAuthorization) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }
    return fetch(apiEndpoint(path), {
      ...init,
      // Cross-origin (portal.systemlabs.com → api.systemlabs.com) fetches
      // strip cookies by default. Include them so the backend can read
      // `exp_assignments` (set by system-web on `.systemlabs.com`) and
      // snapshot it into experiment_assignments on the first authed call.
      // Bearer auth still flows via the Authorization header — this is
      // purely so the cookie reaches the backend. Backend CORS already
      // advertises Access-Control-Allow-Credentials: true.
      credentials: "include",
      headers,
    });
  };

  let res = await request(token);
  let payload = res.ok ? {} : await errorPayload(res);

  if (
    !res.ok &&
    !explicitAuthorization &&
    recoverableAuthFailure(res, payload)
  ) {
    const refreshedToken = await sessionAccessToken(true);
    if (refreshedToken) {
      res = await request(refreshedToken);
      payload = res.ok ? {} : await errorPayload(res);
    }
  }

  if (!res.ok) {
    throw new ApiError(
      res.status,
      payload.code ?? "unknown_error",
      payload.code === "missing_bearer"
        ? "Your login session could not be restored. Refresh the page and try again."
        : (payload.message ?? res.statusText),
      payload.details ??
        (payload.validation_errors
          ? { validation_errors: payload.validation_errors }
          : null),
      payload as Record<string, unknown>,
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Open an authenticated streaming response without trying to parse it as JSON. */
export async function apiFetchStream(
  path: string,
  signal: AbortSignal,
): Promise<Response> {
  let token = await sessionAccessToken();
  if (!token) token = await sessionAccessToken(true);

  const request = (accessToken: string | null) =>
    fetch(apiEndpoint(path), {
      signal,
      credentials: "include",
      headers: {
        Accept: "text/event-stream",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    });

  let res = await request(token);
  let payload = res.ok ? {} : await errorPayload(res);
  if (!res.ok && recoverableAuthFailure(res, payload)) {
    const refreshedToken = await sessionAccessToken(true);
    if (refreshedToken) {
      res = await request(refreshedToken);
      payload = res.ok ? {} : await errorPayload(res);
    }
  }
  if (!res.ok) {
    throw new ApiError(
      res.status,
      payload.code ?? "unknown_error",
      payload.message ?? res.statusText,
      payload.details,
    );
  }
  return res;
}

/**
 * Fetch an arbitrary endpoint and trigger a browser download with the
 * response body. Attaches the same Supabase bearer that apiFetch uses, so
 * admin-gated CSV exports work without a separate auth flow.
 */
export async function apiDownload(
  path: string,
  filename: string,
): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;

  const res = await fetch(`${API_URL}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  if (!res.ok) {
    throw new ApiError(res.status, "download_failed", res.statusText);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Fetch a private binary API resource with the current Supabase bearer. */
export async function apiBlob(path: string): Promise<Blob> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  const target = path.startsWith("/api/v1/")
    ? `${API_URL.replace(/\/api\/v1\/?$/, "")}${path}`
    : `${API_URL}${path}`;
  const res = await fetch(target, {
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) {
    throw new ApiError(res.status, "blob_fetch_failed", res.statusText);
  }
  return res.blob();
}
